<template>
    <rounded-container variant="dark">
        <template v-slot:title>From last conviction change <span class="text-secondary">{{ formatTimeFromISO(currentAsset.meta.conviction_change_time + 'Z') }}</span></template>

        <b-form @submit="onSubmit" class="p-3">
            <b-row>
                <b-col>
                    <b-form-group id="meta-conviction" label="Conviction:" label-for="asset-conviction">
                        <b-form-select 
                            id="asset-conviction"
                            v-model="form.conviction" 
                            :options="convictionOptions"
                        >
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group id="meta-vault-target" label="Default buy size" label-for="asset-vault-target">
                        <b-form-select 
                            id="asset-default-number-positions"
                            v-model="form.defaultNumberPositions" 
                            :options="defaultNumberPositionsOptions"
                        >
                        </b-form-select>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group id="meta-vault-bucket" label="Vault bucket:" label-for="asset-vault-bucket">
                        <b-form-select 
                            id="asset-vault-bucket"
                            v-model="form.vaultBucket" 
                            :options="vaultBucketOptions"
                        >
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group id="meta-vault-target" label="Vault target:" label-for="asset-vault-target">
                        <b-form-input 
                            id="asset-vault-target" 
                            v-model="form.vaultTarget" 
                            placeholder="# of shares" 
                            type="number" 
                            step="any"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group id="meta-one-liner" label="Strategy One Liner:" label-for="asset-one-liner">
                        <b-form-textarea 
                            id="asset-one-liner" 
                            v-model="form.oneLiner" 
                            placeholder="One liner"
                            rows="1"
                            max-rows="3"
                        ></b-form-textarea>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group id="meta-buy-size-rules" label="Buy size rules:">
                        <div v-for="(rule, index) in savedRules" :key="index" class="d-flex align-items-center mb-2">
                            <a href="#" class="flex-grow-1" :class="ruleMatches(rule) ? 'text-success' : 'text-secondary'" @click.prevent="openRuleSheet(index)">{{ ruleLabel(rule) }}</a>
                            <b-button variant="outline-danger" size="sm" :disabled="rulesBusy" @click.prevent="deleteRule(index)">&times;</b-button>
                        </div>
                        <div class="mb-2 d-flex align-items-center">
                            <b-button variant="outline-light" size="sm" :disabled="rulesBusy" @click.prevent="openRuleSheet(null)">+ rule</b-button>

                             <div class="text-secondary small ml-2">
                                current:
                                <span v-if="resolvedRuleLabel" class="matched-rule">{{ resolvedRuleLabel }}</span>
                                <span v-else> — default</span>
                            </div>
                        </div>
                       
                    </b-form-group>
                </b-col>
            </b-row>

            <div v-if="buttonLoadingState">
                <b-button type="submit" variant="primary"><b-spinner small type="grow"></b-spinner></b-button>
            </div>
            <div v-else>
                <b-button type="submit" variant="primary">Update {{ currentAsset.symbol }}</b-button>
            </div>
        </b-form>
    </rounded-container>
</template>

<script>

import { mapState } from 'vuex';

import apiMixin from '@/mixins/api';
import formatter from '@/mixins/formatter'
import bucketMixin from '@/mixins/bucketMixin'
import RoundedContainer from '../style/RoundedContainer.vue';
import EditBuySizeRule from '@/components/overlays/EditBuySizeRule.vue';

import { resolveBuySize, buySizeRuleLabel, buySizeRuleMatches, normalizeBuySizeRule } from '@/store/modules/positions';
import { MAX_BUY_SIZE_RULE_POSITIONS } from '@/constants/trading';
import { SHEET_STYLE } from '@/constants/ui';

export default {
  components: { RoundedContainer },
    props: ['currentAsset'],
    name: 'EditAssetMeta',
    mixins: [apiMixin, formatter, bucketMixin],
    data() {
        return {
            form: {
                conviction: null,
                defaultNumberPositions: null,
                vaultBucket: null,
                vaultTarget: null,
                oneLiner: null
            },
            savedMeta: {},
            rulesBusy: false,
            convictionOptions: [
                { value: '100k', text: '100K' },
                { value: 'high', text: 'High' },
                { value: null, text: 'Regular' },
                { value: 'low', text: 'Low' },
                { value: 'snoozed', text: 'Snoozed' }
            ],
            buttonLoadingState: false,
        }
    },
    created() {
        this.savedMeta = this.currentAsset.meta || {};

        if (this.currentAsset.meta) {
            if ('conviction' in this.currentAsset.meta) {
                this.form.conviction = this.currentAsset.meta.conviction;
            }
            if ('vaultBucket' in this.currentAsset.meta) {
                this.form.vaultBucket = this.currentAsset.meta.vaultBucket
            }
            if ('vaultTarget' in this.currentAsset.meta) {
                this.form.vaultTarget = this.currentAsset.meta.vaultTarget
            }
            if ('oneLiner' in this.currentAsset.meta) {
                this.form.oneLiner = this.currentAsset.meta.oneLiner
            }
            if ('defaultNumberPositions' in this.currentAsset.meta) {
                this.form.defaultNumberPositions = this.currentAsset.meta.defaultNumberPositions;
            }
        }
    },
    methods: {
        ruleLabel(rule) {
            return buySizeRuleLabel(rule, this.defaultPositionSize);
        },
        ruleMatches(rule) {
            return buySizeRuleMatches(this.currentAsset, rule);
        },
        openRuleSheet(index) {
            this.$FModal.show(
                {
                    component: EditBuySizeRule,
                    ...SHEET_STYLE
                },
                {
                    currentAsset: this.currentAsset,
                    rule: index === null ? null : normalizeBuySizeRule(this.savedRules[index]),
                    ruleIndex: index,
                    onSave: this.onRuleSave,
                }
            )
        },
        onRuleSave(rule, index) {
            const rules = this.savedRules.map(normalizeBuySizeRule);
            if (index === null) rules.push(rule);
            else rules.splice(index, 1, rule);

            this.saveRules(rules);
        },
        deleteRule(index) {
            const rules = this.savedRules.map(normalizeBuySizeRule);
            rules.splice(index, 1);

            this.saveRules(rules);
        },
        async saveRules(rules) {
            this.rulesBusy = true;

            const response = await this.apiPost(
                '/api/asset/' + this.currentAsset.symbol + '/meta',
                { buySizeRules: rules }
            );

            this.rulesBusy = false;

            if (!response) return;

            this.savedMeta = response.data.meta || {};
            this.$store.dispatch('updateAssets', [response.data]);
        },
        async onSubmit(event) {
            event.preventDefault()

            this.$emit('request-asset-refresh');

            this.buttonLoadingState = true;

            const response = await this.apiPost(
                '/api/asset/' + this.currentAsset.symbol + '/meta',
                this.form
            );

            this.buttonLoadingState = false;

            if (response) {
                this.savedMeta = response.data.meta || {};
                this.$store.dispatch('updateAssets', [response.data]);
            }
        },
    },
    computed: {
        ...mapState({
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        defaultNumberPositionsOptions() {
            const options = [{ value: null, text: 'Default' }];
            for (let i = 1; i <= MAX_BUY_SIZE_RULE_POSITIONS; i++) {
                options.push({ value: i, text: '$' + (i * this.defaultPositionSize) + ' (' + i + ' pos)' });
            }
            return options;
        },
        resolvedBuySize() {
            // preview against the unsaved default-buy-size selection
            const asset = {
                ...this.currentAsset,
                meta: { ...this.currentAsset.meta, defaultNumberPositions: this.form.defaultNumberPositions },
            };
            return resolveBuySize(asset, this.savedRules);
        },
        resolvedRuleLabel() {
            if (!this.resolvedBuySize.rule) return null;
            return buySizeRuleLabel(this.resolvedBuySize.rule, this.defaultPositionSize);
        },
        savedRules() {
            const saved = this.savedMeta.buySizeRules;
            return Array.isArray(saved) ? saved : [];
        },
        vaultBucketOptions() {
            const options = [{ value: null, text: '- None -' }];
            return options.concat(this.bucketFormOptions); 
        } 
    }
}
</script>

<style>

.matched-rule {
    color: #a97ce8;
}

</style>